import { useEffect, useState } from 'react';
import { Pencil, Search, Trash2 } from 'lucide-react';
import { ServiceEditDialog, type ServiceEdit } from '@generated/components/ServiceEditDialog';
import { getAllServiceProspects, setServiceStatus, updateServiceProspect, deleteServiceProspect, SERVICE_STATUSES } from '@generated/server/services';
import { getStaffPass } from '@generated/hooks/staffPass';
import { ServicesFlyer } from '@generated/components/ServicesFlyer';
import { CallSheet } from '@generated/components/CallSheet';

type Row = Awaited<ReturnType<typeof getAllServiceProspects>>['items'][number];
type Status = (typeof SERVICE_STATUSES)[number];

/** Staff area for selling Neighborhood Services display classifieds: prospect list + sales flyer. */
export function ServicesPage() {
  const [tab, setTab] = useState<'list' | 'calls' | 'flyer'>('list');
  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">Neighborhood Services</h2>
      <p className="mt-1 text-sm text-muted-foreground">Prospects for display classified ads: plumbers, electricians, contractors and other local services.</p>
      <div className="mt-5 flex gap-2 border-b-2 border-foreground">
        {(['list', 'calls', 'flyer'] as const).map((t) => <button key={t} type="button" onClick={() => setTab(t)} className={`min-h-11 border-2 border-b-0 border-foreground px-4 text-sm font-bold ${tab === t ? 'bg-foreground text-background' : 'bg-card hover:bg-muted'}`}>{t === 'list' ? 'Prospects' : t === 'calls' ? 'Call sheet' : 'Sales flyer'}</button>)}
      </div>
      <div className="mt-6">{tab === 'list' ? <Prospects /> : tab === 'calls' ? <CallSheet /> : <ServicesFlyer />}</div>
    </div>
  );
}

export const ORDER = ['Movers', 'Plumbing', 'Electrical', 'Construction / Contractor', 'Handyman', 'Cleaning', 'Landscaping', 'Pest Control', 'Real Estate', 'Architecture', 'Health & Medical', 'Pets', 'Other'];
export function category(i: Row) {
  const t = i.serviceType;
  if (t === 'Moving & Storage') return 'Movers';
  if (t && t !== 'Other') return t;
  const n = `${i.notes ?? ''} ${i.name}`;
  if (/^Health & medical/i.test(i.notes ?? '')) return 'Health & Medical';
  if (/^Pets:/i.test(i.notes ?? '')) return 'Pets';
  if (/^Architecture\./i.test(i.notes ?? '')) return 'Architecture';
  if (/real estate|realty/i.test(n)) return 'Real Estate';
  return 'Other';
}

function Prospects() {
  const [items, setItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    let on = true; setError(''); setRefetching(true);
    getAllServiceProspects({ data: { pass: getStaffPass(), q: dq || undefined } })
      .then((r) => { if (on) setItems(r?.items ?? []); })
      .catch((e) => { console.error(e); if (on) setError('Could not load the list.'); })
      .finally(() => { if (on) { setLoading(false); setRefetching(false); } });
    return () => { on = false; };
  }, [dq]);
  const groups = ORDER.map((c) => ({ c, rows: items.filter((i) => category(i) === c) })).filter((g) => g.rows.length);
  const [editing, setEditing] = useState<Row | null>(null);
  async function save(id: string, v: ServiceEdit) {
    await updateServiceProspect({ data: { pass: getStaffPass(), id, ...v } });
    setItems((p) => p.map((i) => (i.id === id ? { ...i, ...v, email: v.email.trim() ? { email: v.email.trim(), label: v.email.trim() } : null } : i)));
  }
  async function remove(i: Row) {
    if (!window.confirm(`Delete ${i.name} from the list?`)) return;
    const prev = items;
    setItems((p) => p.filter((x) => x.id !== i.id));
    try { await deleteServiceProspect({ data: { pass: getStaffPass(), id: i.id, name: i.name } }); } catch (e) { console.error(e); setItems(prev); setError('Could not delete that prospect.'); }
  }
  async function status(id: string, s: Status) {
    const prev = items;
    setItems((p) => p.map((i) => (i.id === id ? { ...i, outreachStatus: s } : i)));
    try { await setServiceStatus({ data: { pass: getStaffPass(), id, status: s } }); } catch (e) { console.error(e); setItems(prev); setError('Could not save that status.'); }
  }

  return (
    <div>
      <label className="relative block max-w-sm"><Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" /><input aria-label="Search businesses" placeholder="Search businesses" value={q} onChange={(e) => setQ(e.target.value)} className="min-h-11 w-full border-2 border-foreground bg-card pl-9 pr-3 text-sm" /></label>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      {loading ? <div className="mt-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse bg-muted" />)}</div>
        : !items.length ? <p className="mt-6 border-2 border-dashed border-foreground p-6 text-sm">No prospects yet.</p>
        : <div className={`mt-4 space-y-8 ${refetching ? 'opacity-60' : ''}`}>
          <nav aria-label="Jump to category" className="flex flex-wrap gap-1.5">{groups.map((g) => <a key={g.c} href={`#cat-${g.c.replace(/\W+/g, '-')}`} className="border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-2 py-1 text-xs font-black uppercase">{g.c} ({g.rows.length})</a>)}</nav>
          {groups.map((g) => (
          <section key={g.c} id={`cat-${g.c.replace(/\W+/g, '-')}`} className="scroll-mt-4">
            <h3 className="mb-2 inline-block border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-3 py-1 text-lg font-black uppercase tracking-wide">{g.c} <span className="text-sm font-bold">· {g.rows.length}</span></h3>
        <ul className="divide-y-2 divide-foreground border-2 border-foreground bg-card">{g.rows.map((i) => (
          <li key={i.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="font-bold">{i.name} {i.serviceType && <span className="ml-1 text-xs font-semibold text-muted-foreground">{i.serviceType}</span>}</p>
              <p className="break-words text-sm">{[i.contactName, i.phone, i.email?.email].filter(Boolean).join(' · ') || <span className="italic text-muted-foreground">No contact info yet</span>}</p>
              {i.address && <p className="text-sm">{i.address}</p>}
              {i.notes && <p className="mt-1 text-xs text-muted-foreground">{i.notes}</p>}
              {i.lastContacted && <p className="mt-1 text-xs text-muted-foreground">Last contacted {i.lastContacted.toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <select aria-label={`Status for ${i.name}`} value={i.outreachStatus ?? 'Not Contacted'} onChange={(e) => status(i.id, e.target.value as Status)} className="min-h-11 min-w-0 flex-1 border-2 border-foreground bg-card px-2 text-sm font-semibold sm:w-44 sm:flex-none">{SERVICE_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              <button type="button" onClick={() => setEditing(i)} aria-label={`Edit ${i.name}`} className="inline-flex min-h-11 items-center gap-1 border-2 border-foreground bg-card px-3 text-sm font-semibold hover:bg-muted"><Pencil className="h-4 w-4" />Edit</button>
              <button type="button" onClick={() => remove(i)} aria-label={`Delete ${i.name}`} className="inline-flex min-h-11 items-center gap-1 border-2 border-foreground bg-card px-3 text-sm font-semibold text-destructive hover:bg-muted"><Trash2 className="h-4 w-4" />Delete</button>
            </div>
          </li>))}</ul>
          </section>))}
        </div>}
      {editing && <ServiceEditDialog row={editing} onClose={() => setEditing(null)} onSave={(v) => save(editing.id, v)} />}
    </div>
  );
}
